'use es6';

import { fromJS, Map as ImmutableMap } from 'immutable';
import { getPropertyFormatter, NumberFormats } from '../../../formatters/propertyFormatter';
import { percent } from 'reporting-data/hydrate/numberFormatter';
import { encodeHTMLEntities } from '../../../formatters/utils';
import { defaultCategoryAxisOptions } from '../../../highcharts/options/axis/categoryAxisOptions';
import { defaultDatetimeAxisOptions } from '../../../highcharts/options/axis/datetimeAxisOptions';
import { defaultHiddenAxisOptions } from '../../../highcharts/options/axis/hiddenAxisOptions';
import { defaultLinearAxisOptions } from '../../../highcharts/options/axis/linearAxisOptions';
import { defaultLogarithmicAxisOptions } from '../../../highcharts/options/axis/logarithmicAxisOptions';
import { AxisTypes } from '../xyRecords';
import { defaultScaffoldAxisOptions } from '../../../highcharts/options/axis/scaffoldAxisOptions';
const DEFAULT_AXIS_OPTIONS = {
  category: defaultCategoryAxisOptions,
  datetime: defaultDatetimeAxisOptions,
  hidden: defaultHiddenAxisOptions,
  linear: defaultLinearAxisOptions,
  scaffold: defaultScaffoldAxisOptions,
  logarithmic: defaultLogarithmicAxisOptions
};
const getDefaultAxisOptions = type => DEFAULT_AXIS_OPTIONS[type.toLowerCase()] || defaultLinearAxisOptions;
export const placePlotLinesFromCategoryValues = (categories, plotLines) => {
  if (!categories || !plotLines) {
    return plotLines;
  }
  return plotLines.map(plotLine => {
    //If value is already set, don't overwrite it
    if (plotLine.get('value') !== undefined) {
      return plotLine;
    }
    const targetCategoryIndex = categories.findIndex(category => category.key === plotLine.get('targetCategoryValue'));
    return plotLine.set('value', targetCategoryIndex !== -1 ? targetCategoryIndex : undefined);
  });
};
export const getAxisOptions = (axes, inverted, stackLabels, useDefaultFormatting = false, seriesStackingType) => {
  return axes.map((currentAxis, index) => {
    const {
      id,
      title,
      type,
      categories,
      property = ImmutableMap(),
      min,
      max,
      startOnTick,
      rules,
      gridLineDashStyle
    } = currentAxis;
    const legacyDatetimeBehavior = type === 'category' && property.get('type') === 'datetime';

    /**
     * Currently this is is used to fix labeling
     * for a scenario where the primary dataset
     * on a comparison report is empty, but the
     * comparison period's dataset is not. Once
     * the BE starts zero-filling dates on the
     * primary dataset when there's no data for
     * the time period, this can be removed.
     */
    const hasEmptyDateCategory = type === 'category' && property.get('type') === 'date' && (categories && categories.size === 0 || !categories);
    const legacyFormatAxisLabel = legacyDatetimeBehavior ? {
      formatter() {
        return categories.getIn([this.pos, 'name']);
      }
    } : {};
    const propertyIsPlainString = property.get('type') === 'string' && !property.getIn(['format', 'isHtml'], false);

    // categories are labels, so dont use a formatter unless it's just to
    // encode HTML entities for plain strings
    let formatAxisLabel;
    let getValueForFormatAxisLabel;
    if (useDefaultFormatting) {
      formatAxisLabel = undefined;
    } else if (!categories && type !== 'datetime' || type === 'number' && property.get('formattingObject')) {
      formatAxisLabel = getPropertyFormatter(property, {
        abbreviate: property.get('type') === NumberFormats.number,
        maxFractionDigits: 2
      });
    } else if (propertyIsPlainString) {
      formatAxisLabel = encodeHTMLEntities;
      getValueForFormatAxisLabel = (axis, pos, value) => {
        // HighCharts falls back to the data index as a default
        // label if a label does not exist in categories. So,
        // when dealing with a comparison report where the
        // comparison dataset may have more data points than
        // the primary dataset (i.e. comparing a current month
        // with 30 days to last month with 31), it will render
        // the index as the axis label. Here we override that
        // to provide a blank label instead as we don't intend
        // to ever rely on the fallback index-based labels.
        const categoriesLength = axis && axis.categories && axis.categories.length || 0;
        const isIndexFallbackLabel = value === pos && pos > categoriesLength - 1;
        return isIndexFallbackLabel ? null : value;
      };
    }
    const defaultAxisOptions = getDefaultAxisOptions(legacyDatetimeBehavior ? 'datetime' : type);
    const isLinearOrLog = type !== AxisTypes.datetime && type !== AxisTypes.category;
    const softScaling = property.get('type') === NumberFormats.percent ? {
      softMax: 0
    } : {};
    return defaultAxisOptions.mergeDeep(fromJS(Object.assign({
      id,
      title: {
        text: title
      },
      categories: legacyDatetimeBehavior ? undefined : categories && categories.map(({
        name
      }) => name) || defaultAxisOptions.get('categories'),
      opposite: index !== 0,
      labels: type === AxisTypes.scaffold || hasEmptyDateCategory ? {
        formatter() {
          return '';
        }
      } : Object.assign({}, legacyFormatAxisLabel, formatAxisLabel ? {
        formatter() {
          const value = getValueForFormatAxisLabel ? getValueForFormatAxisLabel(this.axis, this.pos, this.value) : this.value;
          if (seriesStackingType === 'percent') {
            return `${percent(value)}`;
          }
          return formatAxisLabel(value);
        }
      } : {}),
      stackLabels: stackLabels && formatAxisLabel ? {
        enabled: true,
        formatter() {
          const value = this.total;
          return value === 0 ? '' : formatAxisLabel(value);
        }
      } : {},
      min,
      max
    }, min === undefined ? {} : {
      softMin: undefined
    }, max === undefined ? {} : {
      softMax: undefined
    }, startOnTick === undefined ? {} : {
      startOnTick
    }, {
      plotLines: rules && placePlotLinesFromCategoryValues(categories, rules.get('plotLines')),
      plotBands: rules && rules.get('plotBands'),
      reversed: inverted && !isLinearOrLog
    }, softScaling, gridLineDashStyle === undefined ? {} : {
      gridLineDashStyle
    })));
  });
};